import { Link, useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '../store/auth.ts';
import { AppSwitcher } from './AppSwitcher.tsx';
import { Icon } from './Icon.tsx';
import { dv } from '../i18n/dv.ts';

type SideItem = { to: string; label: string; icon: string; matchPrefix?: string };

export function Sidebar(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (!user) return null;
  const items: SideItem[] = [
    { to: '/', label: dv.nav.dashboard, icon: 'dashboard' },
    { to: '/letters', label: dv.nav.letters, icon: 'description', matchPrefix: '/letters' },
    ...(user.isOrgAdmin ? [{ to: '/settings', label: dv.nav.settings, icon: 'tune', matchPrefix: '/settings' }] : []),
    { to: '/profile', label: dv.nav.profile, icon: 'person' },
  ];

  const isActive = (item: SideItem): boolean =>
    item.matchPrefix ? pathname.startsWith(item.matchPrefix) : pathname === item.to;

  const rowBase = 'flex items-center gap-4 rounded-full px-4 py-2.5 text-[0.95rem] transition-colors';

  return (
    <aside
      aria-label={dv.nav.dashboard}
      className="hidden md:flex fixed start-0 top-16 bottom-0 z-30 w-64 flex-col gap-1 px-3 py-4 bg-[hsl(var(--chrome))] overflow-y-auto"
    >
      <Link
        to="/letters/new"
        className="mb-3 inline-flex w-fit items-center gap-3 rounded-2xl bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))] py-3.5 ps-4 pe-6 shadow-sm hover:shadow-md transition-shadow"
      >
        <Icon name="add" size={24} />
        <span className="text-[0.95rem] font-medium">{dv.letters.write}</span>
      </Link>

      <nav className="flex flex-col gap-0.5">
        {items.map((item) => {
          const active = isActive(item);
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={active ? 'page' : undefined}
              className={[
                rowBase,
                active
                  ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] font-medium'
                  : 'text-foreground hover:bg-black/[0.05] dark:hover:bg-white/[0.06]',
              ].join(' ')}
            >
              <Icon name={item.icon} size={22} fill={active} className={active ? '' : 'text-muted-foreground'} />
              <span className="flex-1 truncate">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Ecosystem jump */}
      <div className="mt-auto flex flex-col gap-0.5 pt-2">
        <AppSwitcher variant="sidebar" />
        <a href="https://office.wapplify.app" className={`${rowBase} text-foreground hover:bg-black/[0.05] dark:hover:bg-white/[0.06]`}>
          <span className="grid size-[22px] shrink-0 place-items-center">
            <svg width="20" height="20" aria-hidden="true" viewBox="0 0 48 48">
              <rect width="48" height="48" rx="13.5" fill="#006A60" />
              <path d="M9 15.5 17.5 33 24 21.5" fill="none" stroke="#9EF2E4" strokeWidth={5.6} strokeLinecap="round" strokeLinejoin="round" />
              <path d="M24 21.5 30.5 33 39 15.5" fill="none" stroke="#fff" strokeWidth={5.6} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="flex-1 truncate">ވެޕްލިފައި އޮފީސް</span>
          <Icon name="open_in_new" size={16} className="text-muted-foreground" />
        </a>
      </div>
    </aside>
  );
}
